import React, {MouseEvent, ReactElement} from "react";
import {throttle} from "throttle-debounce";

import {
  css,
  generateKeyId,
  hideWindowScroll,
  inertContent,
  restoreWindowScroll,
  visibleForLocale,
} from "/src/utils/utils";
import translate from "/src/utils/translate";
import {LOCALE} from "/src/config";

import Icon from "../../icon/Icon";
import ModuleBody from "../../layouts/module-body/ModuleBody";
import CTAButton, {CTA} from "../../buttons/cta-button/CTAButton";
import LinkTag from "../../buttons/link-tag/LinkTag";

import FlexportLogo from "/src/svg/flexport-light.svg";
import FlexportCNLogo from "/src/svg/flexport-cn-light.svg";
import SearchIcon from "/src/svg/search.svg";
import MobileLanguageSelector from "../mobile-language-selector/MobileLanguageSelector";
import MobileNavigationItem from "../mobile-navigation-item/MobileNavigationItem";
import {LayoutContext} from "../../layouts/layout/Layout";
import {SEARCH_DRAWER_ID} from "../navigation-items/NavigationItems";
import HamburgerMenu from "../../buttons/hamburger-menu/HamburgerMenu";

import * as styles from "./mobile-navigation-items.module.scss";
import MobileSignInSelector from "../mobile-sign-in-selector/MobileSignInSelector";

type NavNodes =
  Queries.ContentfulNavComponentQueryQuery["allContentfulSuperMainNavBars"]["nodes"][0];

interface MobileNavigationItemsProps {
  navTabs: NavNodes["navTabs"];
  primaryCTA?: CTA;
  onSearch: () => void;
  searchVisible: boolean;
  locale?: string;
}

const THROTTLE_DELAY = 300;
const NAV_HEIGHT = 60;
export const NAV_DRAWER_ID = "mobile-nav-drawer";

const MobileNavigationItems = (
  props: MobileNavigationItemsProps
): ReactElement => {
  const {
    primaryCTA,
    navTabs,
    onSearch,
    searchVisible = false,
    locale = LOCALE,
  } = props;

  const isCN = locale === "zh-CN";
  const [navOpen, setNavOpen] = React.useState<boolean>(false);
  const [mounted, setMounted] = React.useState<boolean>(false);
  const [navIndex, setNavIndex] = React.useState<number>(-1);
  const [isLanguageSelectorOpen, setIsLangaugeSelectorOpen] =
    React.useState<boolean>(false);
  const [isSignInSelectorOpen, setIsSignInSelectorOpen] =
    React.useState<boolean>(false);
  const focusedNavItem = React.useRef<null | HTMLButtonElement>(null);
  const {hideNavSearch} = React.useContext(LayoutContext);

  const mobileHeaderRef = React.useRef<null | HTMLDivElement>(null);

  const drawerRef = React.useRef<null | HTMLDivElement>(null);

  const navOpenRef = React.useRef<boolean>(false);

  const throttleRef = React.useRef<any>(null);

  // to store current state value in ref
  navOpenRef.current = navOpen;

  const onNavToggle = () => {
    // toggle nav status
    setNavOpen(!navOpen);
  };

  const onNavItemToggle = (idx: number) => {
    // toggle nav status
    setNavIndex(idx);

    // if index is -1, meaning it's not opened
    subNavDrawerToggle(idx !== -1);
  };

  const onDrawerTransitionEnd = () => {
    // this is to avoid funky drawer render being out of viewport on safari
    // event loop 101
    window.requestAnimationFrame(() => {
      if (document.activeElement) {
        (document.activeElement as HTMLElement).blur();
      }

      if (focusedNavItem.current) {
        focusedNavItem.current.focus();
        focusedNavItem.current = null;
      }
    });
  };

  // this is to avoid the flashing glitch of drawer on reload
  const setDrawerVisibility = () => {
    if (drawerRef.current) {
      drawerRef.current.style.visibility = navOpenRef.current
        ? "visible"
        : "hidden";
    }
  };

  // this is to lock nav drawer when sub nav drawer is opened
  const subNavDrawerToggle = (open: boolean) => {
    if (drawerRef.current) {
      drawerRef.current.style.overflowX = "hidden";
      drawerRef.current.style.overflowY = !open ? "auto" : "hidden";
    }
  };

  // we watch on resize event, to compute drawer height, as we need to know
  // the super nav bar height, to compute the correct drawer height
  const onResize = () => {
    if (drawerRef.current) {
      const headerPos = mobileHeaderRef.current
        ? mobileHeaderRef.current.getBoundingClientRect().top
        : 0;
      drawerRef.current.style.height = `${
        window.innerHeight - NAV_HEIGHT - headerPos
      }px`;
    }
  };

  // on mount
  React.useEffect(() => {
    if (drawerRef.current) {
      setDrawerVisibility();
    }

    throttleRef.current = throttle(THROTTLE_DELAY, onResize);

    window.addEventListener("resize", throttleRef.current);

    // reset current drawer lock status
    subNavDrawerToggle(false);

    setMounted(true);

    return () => {
      if (throttleRef.current) {
        window.removeEventListener("resize", throttleRef.current);
      }
    };
  }, []);

  // watch nav open state
  React.useEffect(() => {
    if (navOpen) {
      setDrawerVisibility();
      hideWindowScroll();
      inertContent(true);
    } else {
      restoreWindowScroll();
      inertContent(false);
      setNavIndex(-1);
    }

    // set drawer height
    onResize();
  }, [navOpen]);

  const handleLanguageSelectorOpen = React.useCallback(() => {
    setIsLangaugeSelectorOpen(true);
  }, []);

  const handleLanguageSelectorClose = React.useCallback(() => {
    setIsLangaugeSelectorOpen(false);
  }, []);

  const handleSignInSelectorOpen = React.useCallback(() => {
    setIsSignInSelectorOpen(true);
  }, []);

  const handleSignInSelectorClose = React.useCallback(() => {
    setIsSignInSelectorOpen(false);
  }, []);

  return (
    <div
      ref={mobileHeaderRef}
      className={css(
        styles.mobileNavigationItems,
        mounted ? styles.mobileNavigationItemsMounted : "",
        navOpen ? styles.mobileNavigationItemsOpen : ""
      )}
    >
      <ModuleBody classes={styles.mobileNavigationItems__body}>
        <LinkTag
          ariaLabel="Flexport"
          href="/"
          classes={styles.mobileNavigationItems__logoLink}
        >
          <Icon
            svg={isCN ? FlexportCNLogo : FlexportLogo}
            classes={styles.mobileNavigationItems__logo}
          />
        </LinkTag>
        <div className={styles.mobileNavigationItems__right}>
          {!hideNavSearch && (
            <button
              type="button"
              aria-label={translate(
                searchVisible ? "Close search panel" : "Show search panel"
              )}
              aria-expanded={!searchVisible}
              aria-controls={SEARCH_DRAWER_ID}
              className={css(styles.mobileNavigationItems__right__searchBtn)}
              onClick={onSearch}
            >
              <Icon
                svg={SearchIcon}
                classes={styles.mobileNavigationItems__right__searchIcon}
              />
            </button>
          )}

          {primaryCTA && (
            <CTAButton
              kind="primary"
              darkMode
              small
              classes={styles.mobileNavigationItems__right__CTA}
              cta={primaryCTA}
            />
          )}

          <HamburgerMenu
            darkMode
            ariaControls={NAV_DRAWER_ID}
            ariaLabel={translate("Navigation drawer")}
            open={navOpen}
            onToggleOpen={onNavToggle}
          />
        </div>
      </ModuleBody>

      <div
        onTransitionEnd={setDrawerVisibility}
        ref={drawerRef}
        id={NAV_DRAWER_ID}
        className={css(styles.mobileNavigationItems__navDrawer)}
        aria-hidden={!navOpen}
        tabIndex={navOpen ? 0 : -1}
      >
        {navTabs && (
          <>
            <div className={css(styles.mobileNavigationItems__navDrawer__row)}>
              <div
                className={
                  isLanguageSelectorOpen
                    ? styles.mobileNavigationItemsHidden
                    : ""
                }
              >
                <MobileSignInSelector
                  disabled={!navOpen}
                  onClose={handleSignInSelectorClose}
                  onOpen={handleSignInSelectorOpen}
                />
              </div>

              <div
                className={
                  isSignInSelectorOpen ? styles.mobileNavigationItemsHidden : ""
                }
              >
                <MobileLanguageSelector
                  disabled={!navOpen}
                  onClose={handleLanguageSelectorClose}
                  onOpen={handleLanguageSelectorOpen}
                />
              </div>
            </div>
            <nav>
              <ul
                className={css(styles.mobileNavigationItems__navDrawer__items)}
              >
                {navTabs.map((tab, i) => {
                  const visibleIn: Array<string> =
                    tab && tab.visibleIn
                      ? (tab.visibleIn as Array<string>)
                      : [];

                  if (!visibleForLocale(visibleIn, locale)) {
                    return null;
                  }

                  const elemKey = generateKeyId(
                    `mobile-nav-items-${tab?.tabTitle!}-${i}`
                  );

                  return (
                    <li
                      key={elemKey}
                      role="none"
                      data-role="menuitem"
                      className={css(
                        styles.mobileNavigationItems__navDrawer__item
                      )}
                    >
                      <MobileNavigationItem
                        navTab={tab}
                        disabled={!navOpen}
                        open={navIndex === i}
                        onNavClick={(e: MouseEvent<HTMLButtonElement>) => {
                          focusedNavItem.current =
                            e.target as HTMLButtonElement;
                          if (document.activeElement) {
                            (document.activeElement as HTMLElement).blur();
                          }
                          onNavItemToggle(i);
                        }}
                        onDrawerClose={onNavItemToggle}
                        onDrawerTransitionEnd={onDrawerTransitionEnd}
                        menuIndex={i}
                      />
                    </li>
                  );
                })}
              </ul>
            </nav>
          </>
        )}
      </div>
    </div>
  );
};

export default MobileNavigationItems;
